import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Axios from 'axios';
import { useSnackbar } from 'notistack';
import { useSignOut, useAuthHeader } from 'react-auth-kit';
import { useNavigate } from 'react-router-dom';
import { clearSessionExpiry } from '../utils/useSessionExpiry';

/**
 * Reusable button to open Parent Portal with auto-login
 *
 * @param {string} parentEmail - Parent's email address
 * @param {number|string} consultationNumber - Student's consultation number
 * @param {string} studentUuid - Student's UUID for pre-selection in parent portal
 * @param {number|string} courseId - Course ID for pre-selection in parent portal
 * @param {string} variant - Button variant: 'dropdown' (full width) or 'inline' (compact)
 * @param {object} sx - Additional MUI sx styles
 * @param {boolean} disabled - Additional disabled condition
 * @param {function} onSuccess - Callback after successfully opening portal
 */
const ParentPortalButton = ({
  parentEmail,
  consultationNumber,
  studentUuid,
  courseId,
  variant = 'inline',
  sx = {},
  disabled = false,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const authHeader = useAuthHeader();

  const handleLogout = () => {
    signOut();
    localStorage.removeItem('userName');
    localStorage.removeItem('who');
    clearSessionExpiry();
    navigate('/signin');
  };

  const handleClick = async () => {
    if (!parentEmail || !consultationNumber) {
      enqueueSnackbar('Error: Missing parent email or consultation number.', { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_URL}/api/v1/admin/generate-parent-portal-link`,
        { parent_email: parentEmail, consultation_number: consultationNumber, student_uuid: studentUuid, course_id: courseId },
        { headers: { Authorization: authHeader() } }
      );

      // Open the parent portal in a new tab
      window.open(res.data.url, '_blank');

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        console.error('Error opening parent portal:', err);
        enqueueSnackbar('Error: Cannot open parent portal at the moment.', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = disabled || !parentEmail || !consultationNumber || loading;

  // Dropdown variant - full width, matches other menu items
  if (variant === 'dropdown') {
    return (
      <Button
        color="primary"
        sx={{
          p: 1,
          width: '100%',
          borderRadius: '0px',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          ...sx,
        }}
        onClick={handleClick}
        disabled={isDisabled}
      >
        {loading ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <>
            Parent Portal
            <OpenInNewIcon fontSize="small" />
          </>
        )}
      </Button>
    );
  }

  // Inline variant - compact button
  return (
    <Button
      size="small"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        ...sx,
      }}
      onClick={handleClick}
      disabled={isDisabled}
    >
      {loading ? (
        <CircularProgress size={14} color="inherit" />
      ) : (
        <>
          Parent Portal
          <OpenInNewIcon fontSize="small" />
        </>
      )}
    </Button>
  );
};

export default ParentPortalButton;
